/**
 * 统一页头（08 M2 任务）：brand / 导航 / 语言切换 / 主题切换 / 联系 CTA + 移动导航抽屉。
 * 移植自原型 site.js header()/themeToggle()/mobileNav()——结构/类名/交互原样保留，
 * 接线替换：?lang= → /{locale}/ 路由（LanguageSwitcher 同路径等价映射）。
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { useLocale } from '../lib/locale';
import { useContentTheme } from '../lib/content-theme';
import { copy } from '../lib/i18n';
import { LanguageSwitcher } from './bilingual';

const NAV_ITEMS: { to: string; zh: string; en: string }[] = [
  { to: '', zh: '首页', en: 'Home' },
  { to: '/builders', zh: '创作人', en: 'Builders' },
  { to: '/notices', zh: '公告栏', en: 'Noticeboard' },
  { to: '/about', zh: '关于', en: 'About' },
  { to: '/contact', zh: '联系', en: 'Contact' },
];

const THEME_KEY = 'chronospeak-theme';

/** 读主题偏好（内容页 Light/Dark；默认深色）。供 ContentThemeProvider 用。 */
export function readThemePreference(): boolean {
  try {
    return localStorage.getItem(THEME_KEY) === 'light';
  } catch {
    return false;
  }
}

/** 写主题偏好（toggle 时持久化）。 */
export function writeThemePreference(light: boolean): void {
  try {
    localStorage.setItem(THEME_KEY, light ? 'light' : 'dark');
  } catch {
    // 隐私模式静默
  }
}

export function SiteHeader() {
  const { locale } = useLocale();
  const location = useLocation();
  const { light, toggleTheme } = useContentTheme();
  const [navOpen, setNavOpen] = useState(false);

  // 弹窗打开锁背景滚动（AGENTS §7）
  useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  // Esc 关抽屉
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const isActive = (to: string) =>
    to === '' ? location.pathname === `/${locale}` : location.pathname === `/${locale}${to}`;

  return (
    <header className='site-header'>
      <Link className='brand' to={`/${locale}`}>
        Chronuspeak
      </Link>
      <nav aria-label={copy('nav.home', locale) === '首页' ? '主导航' : 'Main navigation'}>
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={`/${locale}${item.to}`}
            aria-current={isActive(item.to) ? 'page' : undefined}
          >
            {locale === 'zh-CN' ? item.zh : item.en}
          </Link>
        ))}
      </nav>
      <div className='header-tools'>
        <Link className='header-cta' to={`/${locale}/contact`}>
          {locale === 'zh-CN' ? '联系' : 'Contact'}
        </Link>
        <LanguageSwitcher />
        <button
          type='button'
          className='language-button theme-toggle'
          onClick={toggleTheme}
          aria-pressed={light}
          aria-label={light ? (locale === 'zh-CN' ? '切换深色模式' : 'Switch to dark mode') : locale === 'zh-CN' ? '切换浅色模式' : 'Switch to light mode'}
        >
          {light ? '☀' : '☾'}
        </button>
        <button
          type='button'
          className='nav-toggle'
          onClick={() => setNavOpen(true)}
          aria-label={locale === 'zh-CN' ? '打开导航菜单' : 'Open navigation menu'}
          aria-expanded={navOpen}
          aria-haspopup='dialog'
        >
          ☰
        </button>
      </div>

      {/* 移动端导航抽屉（原型 mobileNav：全屏 dialog、锁滚动、Esc/按钮/菜单项可关）。
          原型把 .mobile-nav 直接 append 到 document.body（site.js:277）——必须同样
          portal 到 body：若嵌在 <header> 内，.site-header 的 transform:translateX(-50%)
          会成为 fixed 的包含块把抽屉缩成一条，且移动断点 .site-header nav{display:none}
          后代命中把菜单整体隐藏（2026-09-22 生产走查 P0 实锤）。 */}
      {createPortal(
        <div
          className='mobile-nav'
          role='dialog'
          aria-modal='true'
          aria-label={locale === 'zh-CN' ? '导航菜单' : 'Navigation menu'}
          hidden={!navOpen}
          onClick={(e) => {
            if (e.target === e.currentTarget) setNavOpen(false);
          }}
        >
          <div className='mobile-nav-panel'>
            <div className='mobile-nav-head'>
              <strong>Chronuspeak</strong>
              <button
                type='button'
                className='modal-close'
                onClick={() => setNavOpen(false)}
                aria-label={locale === 'zh-CN' ? '关闭菜单' : 'Close menu'}
              >
                ×
              </button>
            </div>
            <nav>
              {NAV_ITEMS.map((item) => (
                <Link key={item.to} to={`/${locale}${item.to}`} onClick={() => setNavOpen(false)}>
                  {locale === 'zh-CN' ? item.zh : item.en}
                </Link>
              ))}
            </nav>
            <div className='mobile-nav-foot'>
              <Link className='button primary' to={`/${locale}/contact`} onClick={() => setNavOpen(false)}>
                {locale === 'zh-CN' ? '联系小镇' : 'Contact the town'}
              </Link>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
